const API_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Vet API Service
 * Handles all vet-related API operations
 */
export class VetService {
    
    /**
     * Handle common API response logic
     * @param {Response} response - Fetch response object
     * @returns {Promise<Object>} JSON response
     */
    static async handleResponse(response) {
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return response.json();
    }

    /**
     * Get all appointments
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Array>} Array of appointments
     */
    static async getAllAppointments(authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/vet/appointments`);
        return this.handleResponse(response);
    }

    /**
     * Get appointments for a specific dog
     * @param {string} dogId - ID of dog
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Array>} Array of appointments
     */
    static async getAppointmentsByDog(dogId, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/vet/appointments/dog/${dogId}`);
        return this.handleResponse(response);
    }

    /**
     * Get single appointment by ID
     * @param {string} appointmentId - ID of appointment
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Appointment details
     */
    static async getAppointmentById(appointmentId, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/vet/appointments/${appointmentId}`);
        return this.handleResponse(response);
    }

    /**
     * Create a new appointment
     * @param {Object} appointmentData - Appointment data
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Created appointment
     */
    static async createAppointment(appointmentData, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/vet/appointments`, {
            method: 'POST',
            body: JSON.stringify(appointmentData)
        });
        return this.handleResponse(response);
    }

    static async createPuppySchema(data, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/vet/schema/puppy`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return this.handleResponse(response);
    }

    static async createAdultSchema(data, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/vet/schema/adult`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return this.handleResponse(response);
    }

    /**
     * Update an existing appointment
     * @param {string} appointmentId - ID of appointment to update
     * @param {Object} updateData - Updated data
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Updated appointment
     */
    static async updateAppointment(appointmentId, updateData, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/vet/appointments/${appointmentId}`, {
            method: 'PUT',
            body: JSON.stringify(updateData)
        });
        return this.handleResponse(response);
    }

    /**
     * Delete an appointment
     * @param {string} appointmentId - ID of appointment to delete
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<void>}
     */
    static async deleteAppointment(appointmentId, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/vet/appointments/${appointmentId}`, {
            method: 'DELETE'
        });
        return this.handleResponse(response);
    }

    /**
     * Upload a file attachment to an appointment
     * @param {string} appointmentId
     * @param {File} file
     * @param {Function} authorizedFetch
     */
    static async uploadAttachment(appointmentId, file, authorizedFetch) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await authorizedFetch(
            `${API_URL}/api/vet/appointments/${appointmentId}/attachments`,
            { method: 'POST', body: formData, isFormData: true }
        );
        return this.handleResponse(response);
    }

    /**
     * Delete an attachment from an appointment
     * @param {string} appointmentId
     * @param {string} attachmentId
     * @param {Function} authorizedFetch
     */
    static async deleteAttachment(appointmentId, attachmentId, authorizedFetch) {
        const response = await authorizedFetch(
            `${API_URL}/api/vet/appointments/${appointmentId}/attachments/${attachmentId}`,
            { method: 'DELETE' }
        );
        return this.handleResponse(response);
    }
}

// Maintaining default export for backward compatibility during transition
export default VetService;